import { Loading } from "@/components/Loading/Loading";
import Overview from "@/components/Overview/Overview";
import { useAuth } from "@/hooks/auth";
import { useUploadsQuery } from "@/hooks/uploads";
import { groupBy } from "@/utils/misc";
import Search from "@ingka/search";
import { UploadWithSlimAssetsDto } from "generated";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ADMIN_COLUMNS, USER_COLUMNS } from "./constants";
import { toRouteError } from "@/errors/routeError";

const OverviewPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { user } = useAuth();
  const navigate = useNavigate();
  const uploadsRequest = useUploadsQuery();
  const [searchTerm, setSearchTerm] = useState<string | null>();

  if (uploadsRequest.isError) {
    throw toRouteError(uploadsRequest.error, { scope: "overview" });
  }

  if (!uploadsRequest.isSuccess) {
    return (
      <div className="grid h-screen place-items-center">
        <Loading text="Loading tickets" />
      </div>
    );
  }

  const uploads = uploadsRequest.data.data;
  const uploadGroups = groupBy(uploads, "state");
  const navigationPath = (uploadId: number): string => {
    const upload = uploads.find((upload) => upload.id === uploadId);
    if (upload?.state === "DRAFT") {
      return `/uploads/${uploadId}/confirm-files`;
    }
    if (user.isAdmin) {
      return `/admin/${uploadId}`;
    }
    return `/uploads/${uploadId}/comments`;
  };

  const uploadFilter = (upload: UploadWithSlimAssetsDto): boolean => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    const uploadIdStr = upload.id.toString();

    if (
      uploadIdStr.includes(searchLower) ||
      (searchLower.startsWith("id") &&
        uploadIdStr.includes(searchLower.slice(2)))
    ) {
      return true;
    }

    const firstNameLower = upload.user.firstName.toLowerCase();
    const lastNameLower = upload.user.lastName.toLowerCase();
    if (
      firstNameLower.includes(searchLower) ||
      lastNameLower.includes(searchLower) ||
      `${firstNameLower} ${lastNameLower}`.includes(searchLower)
    ) {
      return true;
    }

    return upload.assets.some(
      (asset) =>
        asset.id.toString() === searchLower ||
        asset.filename.toLowerCase().includes(searchLower) ||
        asset.name?.toLowerCase().includes(searchLower),
    );
  };

  const columns = (user.isAdmin ? ADMIN_COLUMNS : USER_COLUMNS).map(
    ({ keys, title, subTitle }) => ({
      title,
      subTitle,
      uploadsGroup: keys.map(
        (key) => uploadGroups[key]?.filter(uploadFilter) || [],
      ),
    }),
  );

  const searchLabel = user.isAdmin
    ? "Search by name, ID or asset name"
    : "Search by ID or asset name";

  return (
    <div className="no-body-scroll">
      <Search
        id="search-overview"
        onChange={(_, data) => setSearchTerm(data.value)}
        onClear={() => setSearchTerm(null)}
        placeholder={searchLabel}
        aria-label={searchLabel}
        className="m-space150 mt-space200"
      />
      <Overview
        columns={columns}
        onClick={(uploadId) => {
          navigate(navigationPath(uploadId));
        }}
      />
    </div>
  );
};

export default OverviewPage;
